import {
    GUNB_EXCERPT_LABEL,
    countLabel,
    dateLong,
    fmtPricePerM2,
    gunbExcerptQuote,
    renderInfoHelpButton,
    transactionAreaLabel,
    transactionPricePerM2Help,
    transactionPricePerM2Label
} from '../utils/formatters';
import { guideLine } from '../../shared/guide-link.js';

export function createListPanelRenderers(config) {
    var state = config.state;

    // Parsed address for display, or '' when there is no real address (parseAddr
    // returns the em-dash placeholder '—' for empty input — we don't want it shown).
    function displayAddress(raw) {
        var text = config.parseAddr(raw);
        return text && text !== '—' ? text : '';
    }

    function buildCombinedMetaParts(parts) {
        return parts.filter(Boolean).join(' · ');
    }

    function renderCombinedSectionHeader(label, count, modifierClass) {
        if (modifierClass === 'nm-combined-section-header--t') {
            return '<div class="nm-combined-section-header nm-combined-section-header--t">' +
                '<span class="nm-combined-section-dot nm-combined-section-dot--t"></span>' +
                label + ' (' + count + ')' +
            '</div>';
        }

        if (modifierClass === 'nm-combined-section-header--g') {
            return '<div class="nm-combined-section-header nm-combined-section-header--g">' +
                '<span class="nm-combined-section-dot nm-combined-section-dot--g"></span>' +
                label + ' (' + count + ')' +
            '</div>';
        }

        return '<div class="nm-combined-section-header">' + label + ' (' + count + ')</div>';
    }

    function getListSummaryMessage() {
        var zoom = config.mapZoom();
        if (config.LIMITED_MODE && zoom < config.INDIVIDUAL_ZOOM_THRESHOLD) {
            return {
                title: 'Transakcje w aktualnym widoku',
                meta: 'Przybliż mapę, aby wczytać listę',
                html: renderZoomInState(
                    'Przybliż mapę, aby zobaczyć pojedyncze transakcje',
                    'Skupiska transakcji zostają na mapie. Lista i szczegóły pojawią się po przybliżeniu.'
                )
            };
        }

        var latestGeoData = state.getLatestGeoData();
        var stats = latestGeoData && latestGeoData.stats ? latestGeoData.stats : { count: 0 };
        var count = Number(stats.count) || 0;
        var countHelp = 'Liczba transakcji w skupiskach widocznych teraz na mapie\n'
            + 'Obliczenie: suma liczb ze wszystkich skupisk w widoku, po aktywnych filtrach — domyślnie transakcje od 2023 roku. Po przybliżeniu mapy zobaczysz je na liście.\n'
            + 'Uwaga: starostwa uzupełniają rejestr z opóźnieniem sięgającym kilku miesięcy, więc ostatnie miesiące są jeszcze niepełne.\n'
            + 'Źródło: Rejestr Cen Nieruchomości — ceny z aktów notarialnych.\n'
            + guideLine('jak-korzystac-z-metroskopu#podstawy', 'opóźnienia Rejestru Cen Nieruchomości');

        return {
            title: 'Transakcje w aktualnym widoku',
            meta: count ? countLabel(count, 'transakcja', 'transakcje', 'transakcji') + ' w widoku' : 'Przybliż mapę, aby wczytać listę',
            html: '<div class="nm-summary">'
                + '<p class="ms-note nm-summary-note">Przybliż mapę, aby zobaczyć pojedyncze transakcje i ich listę.</p>'
                + '<div class="ms-metrics ms-metrics--compact nm-summary-metrics">'
                    + '<div class="ms-metric ms-metric--compact">'
                        + '<p class="ms-metric__label">Transakcje w widoku' + renderInfoHelpButton(countHelp, 'Jak liczymy transakcje w widoku') + '</p>'
                        + '<p class="ms-metric__value">' + config.esc(config.fmt(count)) + '</p>'
                        + '<p class="ms-metric__hint">Rejestr Cen Nieruchomości, po aktywnych filtrach</p>'
                    + '</div>'
                + '</div>'
            + '</div>'
        };
    }

    // Shown while a layer is grouped into clusters: what is missing and how to get it.
    function renderZoomInState(title, body) {
        return '<div class="ms-empty-state nm-empty-state nm-zoom-in-state">'
            + '<strong>' + config.esc(title) + '</strong>'
            + '<span>' + config.esc(body) + '</span>'
        + '</div>';
    }

    function applySelectedStylesInList() {
        var cards = config.listContentEl.querySelectorAll('.nm-list-card[data-tid]');
        cards.forEach(function(card) {
            card.classList.toggle('is-selected', card.getAttribute('data-tid') === state.getSelectedTid());
        });
    }

    function getLokalBreakdown(row) {
        if (!row || Number(row.typ) !== 2 || !row.lokale_breakdown) {
            return null;
        }

        // MapLibre queryRenderedFeatures serializes nested GeoJSON properties.
        // Both API rows and map hits must reach the same card renderer as objects.
        var breakdown = row.lokale_breakdown;
        if (typeof breakdown === 'string') {
            try {
                breakdown = JSON.parse(breakdown);
            } catch (error) {
                return null;
            }
        }

        return breakdown && typeof breakdown === 'object' && Array.isArray(breakdown.groups)
            ? breakdown
            : null;
    }

    function capitalizeLabel(value) {
        var text = String(value || '').trim();
        if (!text) return '';

        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    function titleCaseBreakdownLabel(value) {
        return String(value || '').split(' + ').map(capitalizeLabel).join(' + ');
    }

    function fmtIntegerPl(value) {
        return String(Math.round(Number(value))).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
    }

    function fmtBreakdownNumber(value, round) {
        if (value == null || value === '' || !isFinite(Number(value))) {
            return '';
        }

        return round ? fmtIntegerPl(value) : config.fmt(Number(value));
    }

    function renderLokalBreakdownInline(groups, field, suffix, round, valueClass) {
        if (!Array.isArray(groups) || !groups.length) {
            return '';
        }

        var parts = groups.map(function(group) {
            var value = fmtBreakdownNumber(group[field], round);
            if (!value) {
                return '';
            }

            var label = capitalizeLabel(group.label);
            return '<span><span class="nm-lokal-metric-label">' + config.esc(label) + ':</span> ' +
                '<strong' + (valueClass ? ' class="' + valueClass + '"' : '') + '>' +
                    config.esc(value) + (suffix ? ' ' + suffix : '') +
                '</strong></span>';
        }).filter(Boolean);

        return parts.join('<span class="nm-lokal-inline-sep">,</span> ');
    }

    function renderLokalPricePerM2Lines(groups) {
        if (!Array.isArray(groups) || !groups.length) {
            return '';
        }

        return groups.map(function(group) {
            var value = group.price_per_m2 == null || group.price_per_m2 === '' || !isFinite(Number(group.price_per_m2))
                ? ''
                : fmtPricePerM2(group.price_per_m2);
            if (!value) {
                return '';
            }

            return '<div><span>' + config.esc(capitalizeLabel(group.label)) + ':</span> ' +
                '<strong>' + config.esc(value) + ' zł/m²</strong></div>';
        }).filter(Boolean).join('');
    }

    function transactionTypeLabel(row) {
        return row.typ ? config.typLabel(row.typ) : config.nierLabel(row.nier_rodzaj);
    }

    // The low line of a card: transaction type, deed number, county code and
    // the distance from a picked point. Full words only (no "Teryt", no "ul.").
    function renderTransactionSource(row, distanceLabel) {
        var parts = [];
        if (row.tran_rodzaj_trans != null) {
            parts.push('Rodzaj transakcji: ' + config.esc(String(config.rodzLabel(row.tran_rodzaj_trans)).toLowerCase()));
        }
        if (row.numer) {
            parts.push('numer transakcji ' + config.esc(row.numer));
        }
        if (row.teryt) {
            parts.push('kod terytorialny powiatu ' + config.esc(row.teryt));
        }
        if (distanceLabel) {
            parts.push(config.esc(distanceLabel));
        }
        return parts.length ? '<p class="nm-transaction-card__source">' + parts.join(' · ') + '</p>' : '';
    }

    // The help follows the type of the transaction (a lokal, a building or
    // parcels). price and area name the worked example; a multi-unit deed has
    // none, because its total area mixes different kinds of units. A price per
    // square metre of the own prices of some parcels (fromItems, API
    // `price_per_m2_from_items`) is not the deal price over the area: no example.
    function pricePerM2Help(row, price, area, fromItems) {
        var example = !fromItems && price != null && price !== '' && Number(area) > 0
            ? ' (' + fmtIntegerPl(price) + ' zł ÷ ' + config.fmt(area) + ' m²)'
            : '';
        return transactionPricePerM2Help(row.typ, row.price_per_m2_label, example, '', row.building_share || row.lokal_share, fromItems);
    }

    // A part of a deed published as several transactions is priced by its own
    // price; the price of the whole deed is named apart.
    function deedPartNote(row) {
        if (!row.deed_part) {
            return '';
        }
        var deedPrice = row.deed_price != null && row.deed_price !== '' && isFinite(Number(row.deed_price))
            ? ': ' + fmtIntegerPl(row.deed_price) + ' zł'
            : '';
        return '<span>Część aktu notarialnego, cena całego aktu' + config.esc(deedPrice) + '</span>';
    }

    function renderTransactionTitle(row, addressText, dateLabel) {
        var label = addressText || (transactionTypeLabel(row) + (dateLabel ? ' — transakcja z ' + dateLabel : ' — transakcja'));
        return '<h3 class="nm-transaction-card__title">'
            + '<button class="nm-list-card-select" type="button">' + config.esc(label) + '</button>'
        + '</h3>';
    }

    function renderTransactionTop(row, dateLabel, isoDate) {
        return '<div class="nm-transaction-card__top">'
            + '<span class="ms-badge ms-badge--accent">' + config.esc(transactionTypeLabel(row)) + '</span>'
            + (dateLabel
                ? '<time class="nm-transaction-card__date"' + (isoDate ? ' datetime="' + config.esc(isoDate) + '"' : '') + '>' + config.esc(dateLabel) + '</time>'
                : '<span class="nm-transaction-card__date">brak daty</span>')
        + '</div>';
    }

    function renderTransactionLink(detailHref) {
        return detailHref
            ? '<a class="ms-link nm-card-link" href="' + config.esc(detailHref) + '">Szczegóły transakcji</a>'
            : '';
    }

    function transactionPrice(row) {
        return row.cena_brutto != null && row.cena_brutto !== '' && isFinite(Number(row.cena_brutto))
            ? fmtIntegerPl(row.cena_brutto) + ' zł'
            : 'cena: brak danych';
    }

    // The normalized date of the deed (TransakcjeListService::applyTransactionDate()).
    function transactionIsoDate(row) {
        var source = row.dok_date ? String(row.dok_date).substring(0, 10) : '';
        return /^\d{4}-\d{2}-\d{2}$/.test(source) ? source : '';
    }

    function renderLokalBreakdownCardHtml(row, detailHref, distanceLabel) {
        var breakdown = getLokalBreakdown(row);
        var groups = breakdown && Array.isArray(breakdown.groups) ? breakdown.groups : [];
        if (groups.length === 1) {
            // One kind of unit: the act price is that unit's price, so the card
            // reads like a single transaction (no repeated "Cena lokali").
            var group = groups[0];
            return renderSingleTransactionCard(row, detailHref, distanceLabel, {
                area: group.area,
                pricePerM2: group.price_per_m2,
                // The price the group's price per square metre is the price of.
                examplePrice: group.price,
                functionLabel: group.label,
            });
        }
        var dateLabel = config.dateDisp(row.dok_date);
        var perM2Html = renderLokalPricePerM2Lines(groups);
        var areaHtml = renderLokalBreakdownInline(groups, 'area', 'm²', false, '');
        var priceHtml = renderLokalBreakdownInline(groups, 'price', 'zł', true, '');
        var lokalAddress = displayAddress(row.adres);

        return '<article class="ms-card ms-card--grid nm-list-card nm-transaction-card ' +
            (row.tran_lokalny_id_iip === state.getSelectedTid() ? 'is-selected' : '') +
            '" data-tid="' + config.esc(row.tran_lokalny_id_iip) + '">' +
            renderTransactionTop(row, dateLabel, transactionIsoDate(row)) +
            renderTransactionTitle(row, lokalAddress, dateLabel) +
            '<p class="nm-transaction-card__price"><strong>' + transactionPrice(row) + '</strong>' +
                '<span>' + (row.deed_part ? 'cena tej części aktu' : 'cena transakcyjna całego aktu') + '</span>' + deedPartNote(row) + '</p>' +
            '<div class="nm-transaction-card__section">' +
                (breakdown.functions_label ? '<div>Skład lokali: <strong>' + config.esc(titleCaseBreakdownLabel(breakdown.functions_label)) + '</strong></div>' : '') +
                (areaHtml ? '<div>' + config.esc(row.lokal_share ? transactionAreaLabel(row.typ, row.area_label) : 'Powierzchnia lokali') + ': ' + areaHtml
                    + (row.lokal_share && row.area_hint ? ' (' + config.esc(row.area_hint) + ')' : '') + '</div>' : '') +
                (priceHtml ? '<div>Cena lokali: ' + priceHtml + '</div>' : '') +
                (perM2Html ? '<div class="nm-transaction-card__per"><span class="nm-transaction-card__per-label">Cena za metr kwadratowy' +
                    renderInfoHelpButton(pricePerM2Help(row, null, null), 'Jak liczymy cenę za metr kwadratowy') + '</span>' + perM2Html + '</div>' : '') +
            '</div>' +
            renderTransactionSource(row, distanceLabel) +
            renderTransactionLink(detailHref) +
        '</article>';
    }

    function renderListCardHtml(row) {
        var detailHref = config.buildDetailUrl(row);
        var distanceLabel = row.distance_m != null && isFinite(Number(row.distance_m))
            ? config.formatDist(Number(row.distance_m)) + ' od punktu'
            : '';
        var lokalBreakdown = getLokalBreakdown(row);
        if (lokalBreakdown) {
            return renderLokalBreakdownCardHtml(row, detailHref, distanceLabel);
        }

        return renderSingleTransactionCard(row, detailHref, distanceLabel, {
            area: row.powierzchnia,
            pricePerM2: row.price_per_m2,
            examplePrice: row.cena_brutto,
            fromItems: !!row.price_per_m2_from_items,
            functionLabel: row.lok_funkcja != null && config.funkcjaLabel ? config.funkcjaLabel(row.lok_funkcja) : '',
        });
    }

    function renderSingleTransactionCard(row, detailHref, distanceLabel, unit) {
        var dateLabel = config.dateDisp(row.dok_date);
        var addressText = displayAddress(row.adres);
        var hasPerM2 = unit.pricePerM2 != null && unit.pricePerM2 !== '' && isFinite(Number(unit.pricePerM2));
        // The price per square metre is the price of the area named in the facts
        // below; its label names that area.
        var perM2Label = transactionPricePerM2Label(row.typ, row.price_per_m2_label);
        var perM2Html = hasPerM2
            ? '<span><span class="sr-only">' + config.esc(perM2Label) + ': </span>' + fmtPricePerM2(unit.pricePerM2) + ' zł/m²' +
                renderInfoHelpButton(pricePerM2Help(row, unit.examplePrice, unit.area, unit.fromItems), perM2Label) + '</span>'
            : '<span>cena za metr kwadratowy: brak danych</span>';

        var facts = [];
        // A building or a lokal sold as a share: what the area of the share is a part of.
        var hasArea = unit.area != null && unit.area !== '';
        facts.push('<li>' + config.esc(transactionAreaLabel(row.typ, row.area_label)) + ' '
            + (hasArea ? config.fmt(unit.area) + ' m²' : 'brak danych')
            + (hasArea && row.area_hint ? ' (' + config.esc(row.area_hint) + ')' : '') + '</li>');
        if (unit.functionLabel) {
            facts.push('<li>Funkcja: ' + config.esc(String(unit.functionLabel).toLowerCase()) + '</li>');
        }

        return '<article class="ms-card ms-card--grid nm-list-card nm-transaction-card ' + (row.tran_lokalny_id_iip === state.getSelectedTid() ? 'is-selected' : '') + '" data-tid="' + config.esc(row.tran_lokalny_id_iip) + '">' +
            renderTransactionTop(row, dateLabel, transactionIsoDate(row)) +
            renderTransactionTitle(row, addressText, dateLabel) +
            '<p class="nm-transaction-card__price"><strong>' + transactionPrice(row) + '</strong>' + perM2Html + deedPartNote(row) + '</p>' +
            '<ul class="nm-transaction-card__facts">' + facts.join('') + '</ul>' +
            renderTransactionSource(row, distanceLabel) +
            renderTransactionLink(detailHref) +
        '</article>';
    }

    function renderGunbListCardHtml(props) {
        var facts = [];
        // The card is a <button>, so its rows are phrasing content (spans).
        if (props.data_wplywu_wniosku) facts.push('Wniosek złożony ' + config.esc(dateLong(props.data_wplywu_wniosku)));
        if (props.data_wydania_decyzji) facts.push('Decyzja wydana ' + config.esc(dateLong(props.data_wydania_decyzji)));
        if (props.numer_decyzji) facts.push('Numer decyzji ' + config.esc(props.numer_decyzji));
        // Only a company or an institution: the API leaves a private investor out.
        if (props.inwestor) facts.push('Inwestor: ' + config.esc(props.inwestor));
        if (props.id_dzialki) facts.push('Działka ' + config.esc(props.id_dzialki));
        return '<button class="ms-card ms-card--grid nm-list-card nm-gunb-card ms-dept--permits' +
            (String(props.id) === String(state.getSelectedGunbId()) ? ' is-selected' : '') +
            '" type="button" data-gunb-id="' + config.esc(String(props.id)) + '">' +
            '<span class="nm-transaction-card__top">' +
                '<span class="ms-badge ms-badge--accent">' + config.esc(props.rodzaj_inwestycji || 'Pozwolenie na budowę') + '</span>' +
                // The category by its name: the register's Roman numeral alone means nothing.
                (props.kategoria_label ? '<span class="nm-transaction-card__date">' + config.esc(props.kategoria_label) + '</span>' : '') +
            '</span>' +
            // The type of works in plain words and the safe excerpt of the register's description.
            (props.roboty ? '<span class="nm-list-address">' + config.esc(props.roboty) + '</span>' : '') +
            (props.opis ? '<span class="nm-card-subline">' + config.esc(GUNB_EXCERPT_LABEL + ': ' + gunbExcerptQuote(props.opis)) + '</span>' : '') +
            (facts.length ? '<span class="nm-gunb-card__facts"><span>' + facts.join('</span><span>') + '</span></span>' : '') +
        '</button>';
    }

    function renderCombinedBrowsePanel() {
        var combinedBrowseTransactions = state.getCombinedBrowseTransactions() || [];
        var combinedBrowseGunb = state.getCombinedBrowseGunb() || [];
        var combinedBrowseOtherHits = state.getCombinedBrowseOtherHits() || [];
        var transactionCount = combinedBrowseTransactions.length;
        var gunbCount = combinedBrowseGunb.length;
        var signalHits = combinedBrowseOtherHits.filter(function(hit) { return hit.type === 'signal'; });
        var noiseHits = combinedBrowseOtherHits.filter(function(hit) { return hit.type === 'noise'; });
        var otoczenieHits = combinedBrowseOtherHits.filter(function(hit) { return hit.type === 'otoczenie'; });
        var buildingHits = combinedBrowseOtherHits.filter(function(hit) { return hit.type === 'building'; });
        var parcelHits = combinedBrowseOtherHits.filter(function(hit) { return hit.type === 'parcel'; });
        var totalCount = transactionCount + gunbCount + combinedBrowseOtherHits.length;

        config.listTitleEl.textContent = 'W tym miejscu: ' + countLabel(totalCount, 'obiekt', 'obiekty', 'obiektów');
        config.listMetaEl.textContent = buildCombinedMetaParts([
            transactionCount ? 'Transakcje: ' + transactionCount : '',
            gunbCount ? 'Pozwolenia: ' + gunbCount : '',
            signalHits.length ? 'Sygnały: ' + signalHits.length : '',
            noiseHits.length ? 'Warstwy hałasu: ' + noiseHits.length : '',
            otoczenieHits.length ? 'Otoczenie: ' + otoczenieHits.length : '',
            buildingHits.length ? 'Budynki: ' + buildingHits.length : '',
            parcelHits.length ? 'Działki: ' + parcelHits.length : ''
        ]);
        config.sortRowEl.style.display = 'none';
        config.listMoreBtn.style.display = 'none';

        var html = '<div class="nm-overlap-back-row">' +
            '<button class="ms-button ms-button--secondary ms-button--sm nm-overlap-back-btn" id="nm-combined-browse-back" type="button">← Wróć do mapy</button>' +
        '</div>';

        if (transactionCount) {
            html += renderCombinedSectionHeader('Transakcje', transactionCount, 'nm-combined-section-header--t');
            html += '<div class="nm-list-stack">';
            config.sortOverlapFeatures(combinedBrowseTransactions, state.getCurrentSort()).forEach(function(feature) {
                html += renderListCardHtml((feature && feature.properties) || {});
            });
            html += '</div>';
        }

        if (gunbCount) {
            html += renderCombinedSectionHeader('Pozwolenia na budowę', gunbCount, 'nm-combined-section-header--g');
            html += '<div class="nm-list-stack">';
            combinedBrowseGunb.forEach(function(feature) {
                html += renderGunbListCardHtml((feature && feature.properties) || {});
            });
            html += '</div>';
        }

        [
            { label: 'Hałas', hits: noiseHits },
            { label: 'Sygnały społeczności', hits: signalHits },
            { label: 'Otoczenie', hits: otoczenieHits },
            { label: 'Budynki', hits: buildingHits },
            { label: 'Działki', hits: parcelHits }
        ].forEach(function(section) {
            if (!section.hits.length) {
                return;
            }

            html += renderCombinedSectionHeader(section.label, section.hits.length);
            html += '<div class="nm-list-stack">';
            section.hits.forEach(function(hit) {
                html += config.renderTapHitCardHtml(hit);
            });
            html += '</div>';
        });

        config.listContentEl.innerHTML = html;
        config.listContentEl.__listContentHtml = null;
        applySelectedStylesInList();
    }

    function renderGunbBrowsePanel() {
        var gunbBrowseFeatures = state.getGunbBrowseFeatures();
        config.listTitleEl.textContent = countLabel(gunbBrowseFeatures.length, 'pozwolenie', 'pozwolenia', 'pozwoleń') + ' na tej działce';
        config.listMetaEl.textContent = 'Wybierz pozwolenie z listy, aby zobaczyć szczegóły';
        config.sortRowEl.style.display = 'none';
        config.listMoreBtn.style.display = 'none';

        var html = '<div class="nm-overlap-back-row">' +
            '<button class="ms-button ms-button--secondary ms-button--sm nm-overlap-back-btn" id="nm-gunb-browse-back" type="button">← Wróć do mapy</button>' +
        '</div>';
        html += '<div class="nm-list-stack">';
        gunbBrowseFeatures.forEach(function(feature) {
            html += renderGunbListCardHtml((feature && feature.properties) || {});
        });
        html += '</div>';
        config.listContentEl.innerHTML = html;
        config.listContentEl.__listContentHtml = null;
    }

    function applySelectedStylesInGunbList() {
        var cards = config.listContentEl.querySelectorAll('.nm-list-card[data-gunb-id]');
        cards.forEach(function(card) {
            card.classList.toggle('is-selected', card.getAttribute('data-gunb-id') === String(state.getSelectedGunbId()));
        });
    }

    return {
        applySelectedStylesInGunbList: applySelectedStylesInGunbList,
        applySelectedStylesInList: applySelectedStylesInList,
        getListSummaryMessage: getListSummaryMessage,
        renderCombinedBrowsePanel: renderCombinedBrowsePanel,
        renderGunbBrowsePanel: renderGunbBrowsePanel,
        renderGunbListCardHtml: renderGunbListCardHtml,
        renderListCardHtml: renderListCardHtml,
    };
}
